import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ClientPicker from "../ClientPicker";
import AddressAutocomplete from "../AddressAutocomplete";
import { Siren, Warning } from "@phosphor-icons/react";

const SLA_PRESETS = [1, 2, 4];

/** Raise a Critical emergency job from an inbox email — prefilled with what the AI read (address, SLA, site contact). */
export function RaiseEmergencyDialog({ item, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const em = item.emergency || {};
    const addr = em.address && !em.address.address_unconfirmed ? em.address : {};
    setForm({
      title: (item.subject || "").replace(/^(re|fw|fwd):\s*/i, "").slice(0, 200), response_hours: em.sla?.hours || 4,
      street_address: addr.street_address || "", town_city: addr.town_city === "TBC" ? "" : (addr.town_city || ""), county: addr.county || "", postcode: addr.postcode || "",
      site_contact_name: em.site_contact_name || "", site_contact_phone: em.site_contact_phone || "", access_notes: em.access_notes || "",
      description: item.body_text || "", client_id: item.client_id || "", client_name: item.client_name || item.from_name || "", client_email: item.client_id ? "" : item.from_email,
      client_company: item.client_company || "", client_phone: "", requester_contact_id: item.contact_id || "", requester_contact_name: item.contact_name || "", requester_contact_color: "",
    });
  }, [open, item]);

  if (!form) return null;
  const em = item.emergency || {};
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addressGiven = !!(form.street_address.trim() || form.postcode.trim());
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (addressGiven && (!form.street_address.trim() || !form.postcode.trim())) { toast.error("Enter both the street address and postcode — or clear both to raise it as 'Address to be confirmed'"); return; }
    setBusy(true);
    try {
      const { data } = await API.post(`/inbox/${item.id}/raise-emergency`, { ...form, response_hours: Number(form.response_hours) || 4 });
      toast.success(`Emergency ${data.reference} raised — ${data.response_hours}h SLA${data.address_unconfirmed ? " · address to be confirmed" : ""}${data.ack?.sent ? " · sender acknowledged" : ""}`);
      onDone?.(data);
      onOpenChange(false);
      navigate(`/emergency-jobs/${data.emergency_job_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="raise-emergency-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2 text-red-700 dark:text-red-400"><Siren size={18} weight="fill" /> Raise emergency job</DialogTitle>
          <DialogDescription>From {item.from_name ? `${item.from_name} <${item.from_email}>` : item.from_email}. Logged as <strong>Critical</strong>; the SLA clock starts now and the sender gets an acknowledgement with the EMG reference.</DialogDescription>
        </DialogHeader>
        {em.ai?.genuine_emergency === false && (
          <p className="text-xs rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-3 py-2 flex items-start gap-2" data-testid="raise-emergency-ai-warning">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0" /> <span>AI thinks this may be a routine mention of “emergency”{em.ai.reason ? ` — ${em.ai.reason}` : ""}. Raise it anyway if in doubt.</span>
          </p>
        )}
        <div className="space-y-4">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => set("title", e.target.value)} className="mt-1" data-testid="raise-emergency-title" /></div>
          <ClientPicker value={form} onChange={setForm} testIdPrefix="raise-emergency-client" />
          <div>
            <Label className="text-xs">Response SLA (hours)</Label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {SLA_PRESETS.map(h => (
                <button key={h} type="button" onClick={() => set("response_hours", h)}
                  className={`h-9 px-3 rounded-md border text-xs font-semibold transition-colors ${Number(form.response_hours) === h ? "bg-red-600 text-white border-red-600" : "border-border hover:bg-muted"}`}
                  data-testid={`raise-emergency-sla-${h}`}>{h}h</button>
              ))}
              <Input type="number" min="0.5" step="0.5" value={form.response_hours} onChange={e => set("response_hours", e.target.value)} className="w-24 h-9" data-testid="raise-emergency-sla-hours" />
              <span className="text-[11px] text-muted-foreground">
                {em.sla?.source === "email" ? `Email asked for “${em.sla.phrase}”` : em.sla?.source === "asap" ? `Email said “${em.sla.phrase}” → 2h` : "Standard emergency response is 4h"}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Site address {em.address?.address_source === "email" ? <span className="text-emerald-700 font-normal">· read from the email</span> : em.address?.address_source === "client_property" ? <span className="text-sky-700 font-normal">· the client's property</span> : <span className="text-amber-700 font-normal">· not found in the email — leave blank to raise as “Address to be confirmed”</span>}</Label>
            <AddressAutocomplete placeholder="Search a postcode or address to fill the fields…" data-testid="raise-emergency-address-search"
              onSelect={(a) => setForm(f => ({ ...f, street_address: a.street_address || f.street_address, town_city: a.town_city || f.town_city, county: a.county || f.county, postcode: a.postcode || f.postcode }))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={form.street_address} onChange={e => set("street_address", e.target.value)} placeholder="Street address" data-testid="raise-emergency-street" />
              <Input value={form.town_city} onChange={e => set("town_city", e.target.value)} placeholder="Town / city" data-testid="raise-emergency-town" />
              <Input value={form.county} onChange={e => set("county", e.target.value)} placeholder="County (optional)" data-testid="raise-emergency-county" />
              <Input value={form.postcode} onChange={e => set("postcode", e.target.value)} placeholder="Postcode" data-testid="raise-emergency-postcode" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Site contact</Label><Input value={form.site_contact_name} onChange={e => set("site_contact_name", e.target.value)} placeholder="Name" className="mt-1" data-testid="raise-emergency-contact-name" /></div>
            <div><Label className="text-xs">Site contact phone</Label><Input value={form.site_contact_phone} onChange={e => set("site_contact_phone", e.target.value)} placeholder="Phone" className="mt-1" data-testid="raise-emergency-contact-phone" /></div>
          </div>
          <div><Label className="text-xs">Description (from the email)</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={5} className="mt-1 text-xs" data-testid="raise-emergency-description" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="raise-emergency-submit"><Siren size={14} weight="fill" className="mr-1" /> {busy ? "Raising…" : `Raise emergency · ${Number(form.response_hours) || 4}h SLA`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
